import { inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';

import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Router, ActivatedRoute } from '@angular/router';
import { LoadingService } from './loading.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { UserStore } from '../../public/private/_store/user.store';
import { FounderStore } from '../../public/private/founder/_store/founder.store';
import { ProsumerStore } from '../../public/private/prosumer/_store/prosumer.store';
import { InstallatoreStore } from '../../public/private/installatore/_store/installatore.store';
import { TecnicoStore } from '../../public/private/tecnico/_store/tecnico.store';
import { StagingStore } from '../../public/private/_common/staging/_store/staging.store';

export type DecodedToken = JwtPayload & {
  sub?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  realm_access?: { roles: string[] };
  resource_access?: Record<string, { roles: string[] }>;
};

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private _loading = inject(LoadingService);
  // private _toast = inject(MessageService);
  // private _translate = inject(TranslateService);
  private readonly _injector = inject(Injector);

  private readonly API_URL = environment.auth.url; // Cambia con il tuo router Fission
  private accessTokenKey = 'access_token';
  private refreshTokenKey = 'refresh_token';
  private idTokenKey = 'id_token';
  private storageModeKey = 'auth_storage_mode';
  private refreshInterval: any = null;
  private refreshRequest$: Observable<boolean> | null = null;
  private useLocalStorage = true; // 👈 di default "ricordami" attivo
  private socialPopup: Window | null = null;
  private socialListener: ((event: MessageEvent) => void) | null = null;
  private socialState: string | null = null;

  /** Ritorna lo storage attivo */
  private get storage(): Storage {
    return this.useLocalStorage ? localStorage : sessionStorage;
  }

  isAuthenticated$ = new BehaviorSubject<boolean>(this.hasValidAccessToken());

  constructor(private http: HttpClient) {
    this.restoreStorageMode();
  }

  login(username: string, password: string, cookie: boolean = false) {
    this.useLocalStorage = cookie; // 👈 Imposta il tipo di storage
    localStorage.setItem(this.storageModeKey, cookie ? 'local' : 'session');

    return this.http
      .post<TokenResponse>(
        `${this.API_URL}/auth/sessions`,
        { username, password, rememberMe: cookie },
        { withCredentials: true },
      )
      .pipe(
        tap((tokens) => {
          this.handleNewTokens(tokens);
          const redirectUrl =
            this.route.snapshot.queryParamMap.get('redirectTo') ?? '/private';
          if (redirectUrl)
            this.router.navigateByUrl(redirectUrl).then(() => {
              this._loading.stop();
            });
        }),
        map(() => true),
        catchError((e) => {
          return of(e);
        }),
      );
  }

  register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) {
    return this.http
      .post<TokenResponse>(
        `${this.API_URL}/auth/users`,
        {
          email,
          password,
          firstName,
          lastName,
        },
        { withCredentials: true },
      )
      .pipe(
        tap((tokens) => {
          this.handleNewTokens(tokens);
          const redirectUrl =
            this.route.snapshot.queryParamMap.get('redirectTo') ?? '/private';
          if (redirectUrl)
            this.router.navigateByUrl(redirectUrl).then(() => {
              this._loading.stop();
            });
        }),
        map(() => true),
        catchError((e) => {
          // this._toast.add({
          //   key: 'global-toast',
          //   severity: 'error',
          //   summary: this._translate.instant('toast.valid.title'),
          //   detail: e,
          // });
          return of(e);
        }),
      );
  }

  forgot(email: string) {
    const redirectTo = new URLSearchParams(window.location.search).get(
      'redirectTo',
    );

    return this.http
      .post(`${this.API_URL}/auth/password/forgot`, {
        email,
        redirectTo,
      })
      .pipe(
        map(() => true),
        catchError((e) => {
          // this._toast.add({
          //   key: 'global-toast',
          //   severity: 'error',
          //   summary: this._translate.instant('toast.valid.title'),
          //   detail: e,
          // });
          return of(e);
        }),
      );
  }

  reset(token: string, newPassword: string) {
    return this.http
      .post<TokenResponse>(
        `${this.API_URL}/auth/password/reset`,
        {
          newPassword,
          token,
        },
        { withCredentials: true },
      )
      .pipe(
        tap((tokens) => {
          this.handleNewTokens(tokens);
          const redirectUrl =
            this.route.snapshot.queryParamMap.get('redirectTo') ?? '/private';
          if (redirectUrl)
            this.router.navigateByUrl(redirectUrl).then(() => {
              this._loading.stop();
            });
        }),
        map(() => true),
        catchError((e) => {
          // this._toast.add({
          //   key: 'global-toast',
          //   severity: 'error',
          //   summary: this._translate.instant('toast.valid.title'),
          //   detail: e,
          // });
          return of(e);
        }),
      );
  }

  refresh() {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refresh_token = this.getRefreshToken();

    this.refreshRequest$ = this.http
      .post<TokenResponse>(
        `${this.API_URL}/auth/sessions/refresh`,
        refresh_token ? { refresh_token } : {},
        { withCredentials: true },
      )
      .pipe(
        tap((tokens) => this.handleNewTokens(tokens)),
        map(() => true),
        catchError(() => {
          return of(false);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  logout(redirect: string) {
    this._loading.start();
    const refreshToken = this.getRefreshToken();
    const idToken = this.getIdToken();
    const absoluteRedirect = redirect.startsWith('http')
      ? redirect
      : `${window.location.origin}${redirect}`;
    const logoutParams = new URLSearchParams({
      client_id: environment.keycloak.clientId,
      post_logout_redirect_uri: absoluteRedirect,
    });
    if (idToken) {
      logoutParams.set('id_token_hint', idToken);
    }
    const keycloakLogoutUrl =
      `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout?` +
      logoutParams.toString();

    this.clearClientSession();

    this.http
      .post(
        `${this.API_URL}/auth/sessions/current`,
        {
          ...(refreshToken ? { refresh_token: refreshToken } : {}),
          ...(idToken ? { id_token_hint: idToken } : {}),
        },
        { withCredentials: true },
      )
      .pipe(catchError(() => of(null)))
      .subscribe({
        complete: () => {
          // Completa sempre anche il logout SSO lato Keycloak/IdP.
          window.location.href = keycloakLogoutUrl;
        },
      });
  }

  getAccessToken(): string | null {
    return this.readToken(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return this.readToken(this.refreshTokenKey);
  }

  getIdToken(): string | null {
    return this.readToken(this.idTokenKey);
  }

  isLoggedIn$() {
    return this.isAuthenticated$.asObservable();
  }

  private handleNewTokens(tokens: TokenResponse) {
    this.clearTokensFromAllStorages();
    this.storage.setItem(this.accessTokenKey, tokens.access_token);
    if (tokens.refresh_token) {
      this.storage.setItem(this.refreshTokenKey, tokens.refresh_token);
    }
    if (tokens.id_token) {
      this.storage.setItem(this.idTokenKey, tokens.id_token);
    }
    this.isAuthenticated$.next(true);

    // Avvia il timer di refresh basato su expires_in
    this.startRefreshTimer(tokens.expires_in);
  }

  private startRefreshTimer(expiresIn: number) {
    this.clearRefreshTimer();

    // ⏱️ Fai il refresh 30 secondi prima della scadenza
    const refreshTime = (expiresIn - 30) * 1000;

    this.refreshInterval = setInterval(() => {
      this.refresh().subscribe((success) => {
        if (!success) {
          const currentPath = this.router.url;
          this.logout(currentPath);
        }
      });
    }, refreshTime);
  }

  private clearRefreshTimer() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  private hasValidAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  /** Restituisce il JWT decodificato (o null se non presente/valido) */
  getDecodedToken(): DecodedToken | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      return jwtDecode<DecodedToken>(token);
    } catch {
      return null;
    }
  }

  /** Ritorna true se c'è un access token NON scaduto */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const { exp } = jwtDecode<JwtPayload>(token);
      // se exp manca lo consideriamo valido (alcuni issuer custom)
      return !exp ? true : Date.now() / 1000 < exp;
    } catch {
      return false;
    }
  }

  /** Controlla se l'utente ha un ruolo (realm o client). clientId predefinito: 'app' */
  hasRole(role: string, clientId = 'app'): boolean {
    const decoded = this.getDecodedToken();
    if (!decoded) return false;

    const realmRoles = decoded.realm_access?.roles ?? [];
    const clientRoles = decoded.resource_access?.[clientId]?.roles ?? [];

    return [...realmRoles, ...clientRoles].includes(role);
  }

  callbackSocial(tokens: any) {
    this.useLocalStorage = true; // 👈 Imposta il tipo di storage
    localStorage.setItem(this.storageModeKey, 'local');
    this.handleNewTokens(tokens);
    const redirectUrl =
      this.route.snapshot.queryParamMap.get('redirectTo') ?? undefined;
    if (redirectUrl)
      this.router.navigateByUrl(redirectUrl).then(() => {
        this._loading.stop();
      });
  }

  private createSocialState(): string {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  private getStringQueryParam(value: unknown): string | null {
    if (Array.isArray(value)) return value[0] ?? null;
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private getCurrentOrRedirectQueryParam(name: string): string | null {
    const routeValue = this.route.snapshot.queryParamMap.get(name);
    if (routeValue) return routeValue;

    const routerTree = this.router.parseUrl(this.router.url);
    const routerValue = this.getStringQueryParam(routerTree.queryParams[name]);
    if (routerValue) return routerValue;

    const searchParams = new URLSearchParams(window.location.search);
    const searchValue = searchParams.get(name);
    if (searchValue) return searchValue;

    const redirectTo =
      this.route.snapshot.queryParamMap.get('redirectTo') ??
      this.getStringQueryParam(routerTree.queryParams['redirectTo']) ??
      searchParams.get('redirectTo');
    if (!redirectTo) return null;

    const redirectTree = this.router.parseUrl(redirectTo);
    const redirectValue = this.getStringQueryParam(
      redirectTree.queryParams[name],
    );
    if (redirectValue) return redirectValue;

    try {
      return new URL(redirectTo, window.location.origin).searchParams.get(name);
    } catch {
      return null;
    }
  }

  private cleanupSocialLogin() {
    if (this.socialListener) {
      window.removeEventListener('message', this.socialListener);
      this.socialListener = null;
    }

    if (this.socialPopup && !this.socialPopup.closed) {
      this.socialPopup.close();
    }

    this.socialPopup = null;
    this.socialState = null;
  }

  loginWithSocial(provider: string) {
    this.cleanupSocialLogin();
    const state = this.createSocialState();
    const landing = this.getCurrentOrRedirectQueryParam('landing');

    const redirectUri = `${window.location.origin}/login/social/callback`;
    const params = new URLSearchParams({
      client_id: environment.keycloak.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      kc_idp_hint: provider,
      state,
      // Forza nuovo login lato OP per evitare riuso della sessione
      // precedente quando si cambia provider.
      max_age: '0',
      prompt: 'login',
      ...(landing ? { landing } : {}),
    });

    const authUrl =
      `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/auth?` +
      params.toString();

    const logoutParams = new URLSearchParams({
      client_id: environment.keycloak.clientId,
      post_logout_redirect_uri: authUrl,
    });
    const url =
      `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout?` +
      logoutParams.toString();

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    this.socialPopup = window.open(
      url,
      `socialLogin_${state}`,
      `width=${width},height=${height},left=${left},top=${top}`,
    );
    this.socialState = state;

    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'SOCIAL_LOGIN') {
        if (!event.data?.state || event.data.state !== this.socialState) return;

        this.cleanupSocialLogin();
        this.handleNewTokens(event.data);
        const redirectUrl =
          this.route.snapshot.queryParamMap.get('redirectTo') ?? undefined;

        if (redirectUrl)
          this.router.navigateByUrl(redirectUrl).then(() => {
            this._loading.stop();
          });
      }
    };

    this.socialListener = listener;
    window.addEventListener('message', listener);
  }

  private restoreStorageMode() {
    const mode = localStorage.getItem(this.storageModeKey);
    const hasSessionTokens =
      !!sessionStorage.getItem(this.accessTokenKey) ||
      !!sessionStorage.getItem(this.refreshTokenKey);
    const hasLocalTokens =
      !!localStorage.getItem(this.accessTokenKey) ||
      !!localStorage.getItem(this.refreshTokenKey);

    if (mode === 'session') {
      this.useLocalStorage = false;
      return;
    }

    if (mode === 'local') {
      this.useLocalStorage = true;
      return;
    }

    if (hasSessionTokens && !hasLocalTokens) {
      this.useLocalStorage = false;
      return;
    }

    this.useLocalStorage = true;
  }

  private getSecondaryStorage(): Storage {
    return this.useLocalStorage ? sessionStorage : localStorage;
  }

  private readToken(key: string): string | null {
    const token = this.storage.getItem(key);
    if (token) return token;

    const fallbackToken = this.getSecondaryStorage().getItem(key);
    if (!fallbackToken) return null;

    this.useLocalStorage = !this.useLocalStorage;
    return fallbackToken;
  }

  private clearTokensFromAllStorages() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.idTokenKey);
    sessionStorage.removeItem(this.accessTokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
    sessionStorage.removeItem(this.idTokenKey);
  }

  private clearClientSession() {
    this._injector.get(UserStore).clearRete();
    this._injector.get(ProsumerStore).clear();
    this._injector.get(FounderStore).clear();
    this._injector.get(InstallatoreStore).clear();
    this._injector.get(TecnicoStore).clear();
    this._injector.get(StagingStore).clear();

    this.clearTokensFromAllStorages();
    localStorage.removeItem(this.storageModeKey);

    // Pulizia chiavi applicative legate alla sessione utente.
    // 🔥 'user' NON viene rimosso: il profilo scelto deve sopravvivere al
    // logout, così al login successivo si riapre sull'ultimo profilo usato.
    localStorage.removeItem('first');
    localStorage.removeItem('ref');
    localStorage.removeItem('referral');
    localStorage.removeItem('referral_time');

    this.clearRefreshTimer();
    this.isAuthenticated$.next(false);
    this.useLocalStorage = true;
  }
}
